import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  BugReport as BugIcon,
  Lightbulb as FeatureIcon,
  QuestionAnswer as GeneralIcon,
  CameraAlt as ScreenshotIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useFeedbackContext, FeedbackType, FeedbackPriority } from '../../contexts/FeedbackContext';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const feedbackTypeConfig = {
  bug: {
    icon: <BugIcon />,
    color: '#d32f2f',
    label: 'Bug Report',
    placeholder: 'Describe the issue you encountered...',
    showPriority: true,
    showScreenshot: true,
  },
  feature: {
    icon: <FeatureIcon />,
    color: '#1976d2',
    label: 'Feature Request',
    placeholder: 'Describe the feature you would like to see...',
    showPriority: false,
    showScreenshot: false,
  },
  general: {
    icon: <GeneralIcon />,
    color: '#546e7a',
    label: 'General Feedback',
    placeholder: 'Share your thoughts or suggestions...',
    showPriority: false,
    showScreenshot: false,
  },
};

const FeedbackModal: React.FC = () => {
  const { 
    getContextData, 
    captureScreenshot, 
    clearConsoleErrors, 
    isModalOpen,
    closeFeedbackModal,
    prefilledType = 'general',
    prefilledMessage = ''
  } = useFeedbackContext();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(prefilledType);
  const [priority, setPriority] = useState<FeedbackPriority>('medium');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState(prefilledMessage);
  const [includeContext, setIncludeContext] = useState(true);
  const [includeConsoleErrors, setIncludeConsoleErrors] = useState(true);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [captureInProgress, setCaptureInProgress] = useState(false);

  const config = feedbackTypeConfig[feedbackType];

  const handleTypeChange = (newType: FeedbackType) => {
    setFeedbackType(newType);
    // Reset priority for non-bug types
    if (newType !== 'bug') {
      setPriority('medium');
    }
  };

  const handleCaptureScreenshot = async () => {
    setCaptureInProgress(true);
    try {
      const screenshotData = await captureScreenshot();
      if (screenshotData) {
        setScreenshot(screenshotData);
      } else {
        setSubmitError('Could not capture screenshot. This feature may not be supported in your browser.');
      }
    } catch (error) {
      logger.error('Screenshot capture failed:', error);
      setSubmitError('Failed to capture screenshot.');
    } finally {
      setCaptureInProgress(false);
    }
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim()) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const contextData = getContextData();
      
      // Prepare feedback data
      const feedbackData = {
        type: feedbackType,
        priority: feedbackType === 'bug' ? priority : undefined,
        subject,
        message,
        context: includeContext ? {
          user: contextData.user,
          session: {
            ...contextData.session,
            consoleErrors: includeConsoleErrors ? contextData.session.consoleErrors : [],
          },
          environment: contextData.environment,
        } : undefined,
        screenshot: screenshot && feedbackType === 'bug' ? screenshot : undefined,
        timestamp: new Date().toISOString(),
      };

      logger.info('Submitting feedback:', { type: feedbackType, subject });

      const response = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Use cookie-based authentication
        body: JSON.stringify(feedbackData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to submit feedback';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      setSubmitSuccess(true);
      
      // Clear console errors if they were included
      if (includeConsoleErrors && feedbackType === 'bug') {
        clearConsoleErrors();
      }

      // Reset form after short delay
      setTimeout(() => {
        setSubject('');
        setMessage('');
        setScreenshot(null);
        setPriority('medium');
        setSubmitSuccess(false);
        closeFeedbackModal();
      }, 2000);
      
    } catch (error) {
      logger.error('Error submitting feedback:', error);
      setSubmitError((error as Error).message || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSubmitError('');
      setSubmitSuccess(false);
      closeFeedbackModal();
    }
  };

  return (
    <Dialog 
      open={isModalOpen} 
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? false : "sm"}
      fullWidth={!isMobile}
      PaperProps={{
        sx: {
          ...(isMobile ? {
            margin: 0,
            borderRadius: 0,
            height: '100%',
          } : {
            borderRadius: 2,
            maxHeight: '90vh',
          }),
          bgcolor: 'background.paper',
          border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Feedback</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {submitSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Thank you for your feedback! We'll review it shortly.
          </Alert>
        )}
        
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
            {submitError}
          </Alert>
        )}

        <Stack spacing={isMobile ? 2 : 2.5}>
          {/* Feedback Type Selection */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              What type of feedback do you have?
            </Typography>
            <Grid container spacing={isMobile ? 1.5 : 2}>
              {Object.entries(feedbackTypeConfig).map(([type, config]) => (
                <Grid item xs={12} sm={4} key={type}>
                  <Card
                    variant={feedbackType === type ? "elevation" : "outlined"}
                    sx={{
                      cursor: 'pointer',
                      minHeight: isMobile ? 72 : 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: feedbackType === type ? `2px solid ${config.color}` : undefined,
                      backgroundColor: feedbackType === type ? `${config.color}08` : undefined,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4],
                      },
                    }}
                    onClick={() => handleTypeChange(type as FeedbackType)}
                    role="button"
                    tabIndex={0}
                    aria-pressed={feedbackType === type}
                    aria-label={`Select ${config.label} feedback type`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleTypeChange(type as FeedbackType);
                      }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: isMobile ? 1.5 : 2 }}>
                      <Box sx={{ color: config.color, mb: 0.5 }}>
                        {React.cloneElement(config.icon, { sx: { fontSize: isMobile ? '1.75rem' : '2rem' } })}
                      </Box>
                      <Typography variant="body2" fontWeight={feedbackType === type ? 600 : 400}>
                        {config.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Priority (for bugs only) */}
          {config.showPriority && (
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value as FeedbackPriority)}
                label="Priority"
              >
                <MenuItem value="low">Low - Minor issue</MenuItem>
                <MenuItem value="medium">Medium - Affects workflow</MenuItem>
                <MenuItem value="high">High - Significant impact</MenuItem>
                <MenuItem value="critical">Critical - Blocking work</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Subject */}
          <TextField
            fullWidth
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            size="small"
            placeholder="Brief description of your feedback"
          />

          {/* Message */}
          <TextField
            fullWidth
            label="Details"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            multiline
            rows={4}
            placeholder={config.placeholder}
          />

          {/* Screenshot for bugs */}
          {config.showScreenshot && (
            <Box>
              <Button
                variant={screenshot ? "contained" : "outlined"}
                startIcon={captureInProgress ? <CircularProgress size={16} /> : <ScreenshotIcon />}
                onClick={handleCaptureScreenshot}
                disabled={captureInProgress || !!screenshot}
                size={isMobile ? "medium" : "small"}
                fullWidth={isMobile}
                sx={{
                  minHeight: 48,
                  ...(screenshot && {
                    backgroundColor: 'success.main',
                    color: 'success.contrastText',
                    '&:hover': {
                      backgroundColor: 'success.dark',
                    },
                  }),
                }}
              >
                {captureInProgress ? 'Capturing...' : screenshot ? 'Screenshot Ready' : 'Add Screenshot'}
              </Button>
              {screenshot && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                  Screenshot will be included with your report
                </Typography>
              )}
            </Box>
          )}

          {/* Context Options */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Additional Options
            </Typography>
            <Stack spacing={1}>
              <Paper
                variant="outlined"
                sx={{
                  p: isMobile ? 1.5 : 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: 56,
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <InfoIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Include session data
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Browser info and navigation history
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={includeContext}
                  onChange={(e) => setIncludeContext(e.target.checked)}
                  size="medium"
                />
              </Paper>
              
              {feedbackType === 'bug' && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 56,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <BugIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Include console errors
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Recent errors to help debug issues
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={includeConsoleErrors}
                    onChange={(e) => setIncludeConsoleErrors(e.target.checked)}
                    size="medium"
                  />
                </Paper>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !subject.trim() || !message.trim()}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <></>}
        >
          {isSubmitting ? 'Sending...' : 'Send Feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackModal;
